import React, { Suspense } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

interface LazyLoadWrapperProps {
  children: React.ReactNode;
  fallbackMessage?: string;
}

/**
 * LazyLoadWrapper - Provides a consistent loading experience for lazy-loaded components
 * Used with React.lazy() for code-splitting
 */
export const LazyLoadWrapper: React.FC<LazyLoadWrapperProps> = ({
  children,
  fallbackMessage = "Loading...",
}) => {
  return (
    <Suspense
      fallback={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            {fallbackMessage}
          </Typography>
        </Box>
      }
    >
      {children}
    </Suspense>
  );
};

/**
 * PageLoadingFallback - Full page loading state for route transitions
 */
export const PageLoadingFallback: React.FC<{ message?: string }> = ({
  message = "Loading page...",
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh",
      gap: 2,
    }}
  >
    <CircularProgress size={48} />
    <Typography variant="body1" color="text.secondary">
      {message}
    </Typography>
  </Box>
);

export default LazyLoadWrapper;
